import React, { useEffect, useRef, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { IconButton, Tooltip } from "@mui/material"
import { Hearing as HearingIcon, HearingDisabled as HearingDisabledIcon } from "@mui/icons-material"
import { getSharedAudioContext } from "../utils/audioContext"
import remoteAudio from "../services/remoteAudio"

/**
 * Live microphone level meter (+ optional gate-activity LED).
 *
 * Two sourcing modes:
 *  - getLevel: () => ({ rms, peak })  — meter an existing chain's analyser
 *    (services/voiceChain.js getLevel). Preferred: shows the POST-chain level,
 *    i.e. what actually gets encoded.
 *  - stream: MediaStream             — build a private analyser on the shared
 *    AudioContext (used by the pre-join preview where no chain exists).
 *
 * gateSource: a VoiceChain-like object (getGateState/onGateState); when
 * showGate is true a small LED lights while the gate is open and dims while
 * it's holding the floor.
 *
 * The rAF loop mutates element styles directly (no per-frame setState).
 */
const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 0,
  },
  track: {
    position: "relative",
    flex: 1,
    height: 8,
    borderRadius: 4,
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    overflow: "hidden",
  },
  fill: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // Traditional VU segments FIXED to the track (green to 60%, yellow to
    // 85%, red above). The fill is full-width and revealed via clip-path so
    // the color a level reaches is positionally meaningful — a transform
    // scale would squish the whole gradient into the lit portion instead.
    background: `linear-gradient(90deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.main} 60%, #e8c34a 60%, #e8c34a 85%, ${theme.palette.error.main} 85%, ${theme.palette.error.main} 100%)`,
    clipPath: "inset(0 100% 0 0)",
    willChange: "clip-path",
  },
  peak: {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: 2,
    left: 0,
    backgroundColor: "rgba(255, 255, 255, 0.85)",
    willChange: "left",
  },
  gateDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: theme.palette.action.disabled,
    transition: "background-color 120ms, box-shadow 120ms",
  },
  gateOpen: {
    backgroundColor: theme.palette.secondary.main,
    boxShadow: `0 0 6px ${theme.palette.secondary.main}`,
  },
}))

// Map a linear 0..1 level onto the bar using a dB scale (-60..0 dBFS), which
// matches how loudness is perceived far better than the raw amplitude.
const dbFrac = (lin) => {
  if (!(lin > 0)) return 0
  const db = 20 * Math.log10(lin)
  return Math.min(1, Math.max(0, (db + 60) / 60))
}

const MicLevelMeter = ({
  getLevel = null,
  stream = null,
  gateSource = null,
  showGate = false,
  // Optional self-listen: a function returning (a Promise of) the MediaStream
  // to monitor — resolved lazily on toggle, since the monitor capture/chain
  // may be built on demand. While listening, every remote user's audio is
  // ducked so the test is clean; the OUTBOUND mic mute is the stream
  // provider's job (see voiceChain.createSelfListenMonitor).
  getListenStream = null,
  // Fired when the monitor stops (toggle-off or unmount) so the provider can
  // dispose whatever it built for getListenStream.
  onListenStop = null,
}) => {
  const { classes } = useStyles()
  const [listening, setListening] = useState(false)
  const listenElRef = useRef(null)
  const aliveRef = useRef(true)
  // Guards the async toggle: a second click (or unmount) while the monitor
  // stream is still being acquired must not start a stale session.
  const listenSeqRef = useRef(0)

  const stopListening = () => {
    listenSeqRef.current++
    setListening(false)
    remoteAudio.duck(false)
    if (onListenStop) onListenStop()
    const el = listenElRef.current
    if (el) {
      try {
        el.srcObject = null
        el.remove()
      } catch (e) {
        /* noop */
      }
      listenElRef.current = null
    }
  }

  const toggleListening = async () => {
    if (listening) {
      stopListening()
      return
    }
    const seq = ++listenSeqRef.current
    let src = null
    try {
      src = getListenStream ? await Promise.resolve(getListenStream()) : null
    } catch (e) {
      src = null
    }
    if (!aliveRef.current || seq !== listenSeqRef.current) {
      // Unmounted or superseded while acquiring — let the provider clean up.
      if (onListenStop) onListenStop()
      return
    }
    if (!src || src.getAudioTracks().length === 0) {
      if (onListenStop) onListenStop()
      return
    }
    const el = document.createElement("audio")
    el.autoplay = true
    el.srcObject = new MediaStream(src.getAudioTracks())
    document.body.appendChild(el)
    listenElRef.current = el
    remoteAudio.duck(true)
    setListening(true)
  }

  // Never leave a live monitor (or ducked room / muted mic) behind on unmount.
  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
      stopListening()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const fillRef = useRef(null)
  const peakRef = useRef(null)
  const dotRef = useRef(null)
  // Private analyser when metering a raw stream (no chain to read from).
  const ownRef = useRef(null)

  useEffect(() => {
    if (getLevel || !stream || stream.getAudioTracks().length === 0) return undefined
    const ctx = getSharedAudioContext()
    if (!ctx) return undefined
    let source
    let analyser
    try {
      source = ctx.createMediaStreamSource(stream)
      analyser = ctx.createAnalyser()
      analyser.fftSize = 2048
      source.connect(analyser) // analyser is a valid sink; nothing audible
    } catch (e) {
      return undefined
    }
    ownRef.current = { analyser, buf: new Float32Array(analyser.fftSize) }
    return () => {
      ownRef.current = null
      try {
        source.disconnect()
      } catch (e) {
        /* noop */
      }
    }
  }, [stream, getLevel])

  useEffect(() => {
    let raf
    let peakHold = 0
    const tick = () => {
      let rms = 0
      let peak = 0
      if (getLevel) {
        ;({ rms, peak } = getLevel())
      } else if (ownRef.current) {
        const { analyser, buf } = ownRef.current
        try {
          analyser.getFloatTimeDomainData(buf)
          let sum = 0
          for (let i = 0; i < buf.length; i++) {
            const s = buf[i]
            sum += s * s
            const a = s < 0 ? -s : s
            if (a > peak) peak = a
          }
          rms = Math.sqrt(sum / buf.length)
        } catch (e) {
          /* keep zeros */
        }
      }
      const frac = dbFrac(rms)
      // Peak tick holds briefly, then slides back down.
      peakHold = Math.max(dbFrac(peak), peakHold - 0.006)
      if (fillRef.current) {
        fillRef.current.style.clipPath = `inset(0 ${(100 - frac * 100).toFixed(1)}% 0 0)`
      }
      if (peakRef.current) peakRef.current.style.left = `calc(${(peakHold * 100).toFixed(1)}% - 1px)`
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [getLevel])

  // Gate LED: push-driven off the worklet's ~10 Hz activity reports.
  useEffect(() => {
    if (!showGate || !gateSource || !gateSource.onGateState) return undefined
    const apply = (st) => {
      const el = dotRef.current
      if (!el) return
      if (st && st.open) el.classList.add(classes.gateOpen)
      else el.classList.remove(classes.gateOpen)
    }
    apply(gateSource.getGateState ? gateSource.getGateState() : { open: true })
    return gateSource.onGateState(apply)
  }, [showGate, gateSource, classes.gateOpen])

  return (
    <div className={classes.root}>
      <div className={classes.track}>
        <div ref={fillRef} className={classes.fill} />
        <div ref={peakRef} className={classes.peak} />
      </div>
      {showGate && <div ref={dotRef} className={classes.gateDot} title="Gate activity — lit while your voice passes" />}
      {getListenStream && (
        <Tooltip
          title={
            listening
              ? "Stop listening"
              : "Listen to yourself — mutes your mic to the room and everyone else's audio while testing"
          }
        >
          <IconButton size="small" onClick={toggleListening} aria-label="Listen to my microphone">
            {listening ? (
              <HearingDisabledIcon fontSize="small" color="primary" />
            ) : (
              <HearingIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      )}
    </div>
  )
}

export default MicLevelMeter
